/*
 * OPTIMUS LIFECYCLE — one explicit state model for what the page is doing.
 * ---------------------------------------------------------------------------
 * The page used to answer "is this campaign's flight resolvable?" the instant
 * it mounted, before it knew which campaign it was even looking at. With no
 * campaign selected yet there is no contract to be pending, so the guard that
 * covers "campaign chosen, contract still loading" did not apply, and the
 * FLIGHT default resolved to nothing. The page announced a governed hold —
 * in red — on its very first painted frame, then withdrew it a moment later
 * when the campaign list arrived.
 *
 * That is a lie of timing. "I do not know yet" and "I know, and the answer is
 * no" are different states, and only the second one may be shown as a failure.
 *
 * So the question is no longer answered ad hoc at each call site. This module
 * owns the transition, both portals import it, and the rule it enforces is a
 * single sentence:
 *
 *     an error may only be published from a SETTLED state.
 *
 * Settled means: the advertiser is known, the campaign list has arrived, and
 * any selected campaign's contract has come back — resolved or unresolved.
 * Until then the page is loading, and it says so with the neutral treatment.
 *
 * Nothing here is time-based. There is no delay, no debounce and no
 * suppression window: the states are derived from the data the page actually
 * holds, so a genuine failure surfaces the moment it is genuinely known, and
 * an unresolvable flight still reaches ERROR and stays there.
 *
 * Kept byte-identical between Cortex and Quantum.
 */

import { OPTIMUS_CONTRACT_STATE } from "./optimusCampaignContract";

export const OPTIMUS_LIFECYCLE = Object.freeze({
  /* Identity not yet established — no advertiser to scope anything to. */
  INITIALIZING: "INITIALIZING",
  /* Advertiser known; the campaign list and/or the chosen campaign's contract
     are still arriving. Nothing about the report is knowable yet. */
  LOADING_SELECTIONS: "LOADING_SELECTIONS",
  /* Scope is complete and stable; the report request is in flight. */
  LOADING_REPORT: "LOADING_REPORT",
  /* A report is on screen. */
  READY: "READY",
  /* Settled, and there is legitimately nothing to show. */
  EMPTY: "EMPTY",
  /* Settled, and a definite failure applies to the CURRENT scope. */
  ERROR: "ERROR",
});

/* The states in which the page does not yet know its own answer. An error must
   never be published from one of these. */
const UNSETTLED = Object.freeze([
  OPTIMUS_LIFECYCLE.INITIALIZING,
  OPTIMUS_LIFECYCLE.LOADING_SELECTIONS,
]);

/*
 * A campaign's contract is "pending" while it has been asked for and has not
 * come back. IDLE counts as pending only when a campaign is actually selected:
 * with no selection there is nothing to wait for, and treating IDLE as pending
 * would leave the page loading forever on an advertiser with no campaigns.
 */
export function isContractPending({ campaignSelected, contractState }) {
  if (!campaignSelected) return false;
  return (
    contractState !== OPTIMUS_CONTRACT_STATE.RESOLVED &&
    contractState !== OPTIMUS_CONTRACT_STATE.UNRESOLVED
  );
}

/**
 * Resolve the page's lifecycle state from the facts it holds.
 *
 * `terminalError` is the caller's assertion that a definite failure applies to
 * a complete, stable, authorized scope. It is honoured only from a settled
 * state — passing one during initialization cannot produce ERROR, which is the
 * whole point of the model.
 */
export function resolveOptimusLifecycle({
  advertiserId = null,
  selectionsLoading = false,
  campaignSelected = false,
  contractState = OPTIMUS_CONTRACT_STATE.IDLE,
  reportLoading = false,
  hasReport = false,
  terminalError = null,
} = {}) {
  if (!advertiserId) return OPTIMUS_LIFECYCLE.INITIALIZING;

  if (selectionsLoading || isContractPending({ campaignSelected, contractState })) {
    return OPTIMUS_LIFECYCLE.LOADING_SELECTIONS;
  }

  /* Settled from here down: the page knows its scope. */
  if (terminalError) return OPTIMUS_LIFECYCLE.ERROR;
  if (reportLoading) return OPTIMUS_LIFECYCLE.LOADING_REPORT;
  return hasReport ? OPTIMUS_LIFECYCLE.READY : OPTIMUS_LIFECYCLE.EMPTY;
}

/** True once the page knows enough for a failure to mean something. */
export function isSettledLifecycle(lifecycle) {
  return !UNSETTLED.includes(lifecycle);
}

/** True while the neutral loading treatment is the correct thing to render. */
export function isLoadingLifecycle(lifecycle) {
  return (
    lifecycle === OPTIMUS_LIFECYCLE.INITIALIZING ||
    lifecycle === OPTIMUS_LIFECYCLE.LOADING_SELECTIONS ||
    lifecycle === OPTIMUS_LIFECYCLE.LOADING_REPORT
  );
}

/**
 * May this error be shown?
 *
 * The single gate every error surface goes through. An error is publishable
 * only from a settled lifecycle — never during initialization, never while the
 * selections are still arriving.
 */
export function canPublishOptimusError(lifecycle) {
  return lifecycle === OPTIMUS_LIFECYCLE.ERROR;
}

/**
 * Is a FLIGHT selection genuinely unresolvable, as opposed to not-known-yet?
 *
 * The exact question that produced the red flash. It is answerable only from a
 * settled state; before that the honest answer is "not yet", and the caller
 * must hold its request rather than either erroring or querying a different
 * window.
 */
export function resolveFlightHold({ rangeMode, rangeResolved, lifecycle }) {
  const isFlight = rangeMode === "flight";
  if (!isFlight || rangeResolved) {
    return { pending: false, unresolved: false };
  }
  return isSettledLifecycle(lifecycle)
    ? { pending: false, unresolved: true }
    : { pending: true, unresolved: false };
}
